import MainLayout from "../../Layouts/MainLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useAuth from "../../lib/Hooks/useAuth";
import { NotifyWarning } from "../../lib/Notify/notify";

import {
    GetActiveOrders, GetAllOrders, CreateOrders,
    StartTork, ChangeOrderStatus, DeleteOrder, UpdateOrder, BulkUpdatePrios
} from "../../lib/ApiHandler/TorkOrderApi";
import {
    GetTorks, CreateTork, UpdateTork, DeleteTork, AddTorkNote, UpdateTorkNote, UpdateTorkNoteStatus, DeleteTorkNote
} from "../../lib/ApiHandler/TorkApi";
import {
    GetNoteTypes, CreateNoteType, UpdateNoteType, DeleteNoteType
} from "../../lib/ApiHandler/TorkNoteTypeApi";

import {
    setTorkOrders, addTorkOrders, updateTorkOrder, removeTorkOrder
} from "../../state/slices/TorkOrderSlice";
import { setTorks, addTork, updateTork, removeTork } from "../../state/slices/TorkSlice";
import { setNoteTypes } from "../../state/slices/TorkNoteTypeSlice";

import OrdersTable from "./OrdersTable";
import KilnsToEmpty from "./KilnsToEmpty";
import TorksManager from "./TorksManager";
import TorkNotesList from "./TorkNotesList";
import NoteTypeSettings from "./NoteTypeSettings";
import History from "./History";
import CreateOrderForm from "./CreateOrderForm";
import StartTorkModal from "./StartTorkModal";
import OrderHistoryModal from "./OrderHistoryModal";
import SearchInput from "./SearchInput";

const TABS = [
    { key: "orders", label: "Torkar att starta" },
    { key: "empty", label: "Torkar att tömma" },
    { key: "torks", label: "Torkar" },
    { key: "history", label: "Torkstarter" },
];

const TorkPlanning = ({ jwtToken: jwtTokenProp }) => {
    const { jwtToken: jwtTokenHook, hasTorkAccess, isTorkAdmin } = useAuth();
    const jwtToken = jwtTokenProp || jwtTokenHook;
    const dispatch = useDispatch();
    const readOnly = !isTorkAdmin();

    const orders = useSelector(state => state.torkOrders);
    const torks = useSelector(state => state.torks);
    const noteTypes = useSelector(state => state.torkNoteTypes);

    const [activeTab, setActiveTab] = useState("orders");
    const [torkView, setTorkView] = useState("cards"); // "cards" | "notes"
    const [showCreate, setShowCreate] = useState(false);
    const [startOrder, setStartOrder] = useState(null);
    const [historyOrder, setHistoryOrder] = useState(null); // single order object
    const [allOrders, setAllOrders] = useState([]);
    const [orderSearch, setOrderSearch] = useState("");

    /* Debounce ref for bulk prio updates */
    const bulkPrioDebounceRef = useRef(null);
    const pendingPrioListRef = useRef(null);

    useEffect(() => {
        if (!jwtToken) return;
        GetActiveOrders(jwtToken, (res) => dispatch(setTorkOrders(res)));
        GetTorks(jwtToken, (res) => dispatch(setTorks(res)));
        GetNoteTypes(jwtToken, (res) => dispatch(setNoteTypes(res)));
    }, [dispatch, jwtToken]);

    // Load full history when opening the history tab.
    useEffect(() => {
        if (activeTab === "history" && jwtToken) {
            GetAllOrders(jwtToken, (res) => setAllOrders(res));
        }
    }, [activeTab, jwtToken]);

    // Clear the order search when leaving the orders tab so no hidden filter remains.
    useEffect(() => {
        if (activeTab !== "orders") setOrderSearch("");
    }, [activeTab]);

    const activeNoteTorkIds = useMemo(
        () => torks.filter(t => (t.notes || []).some(n => n.status === "planned" || n.status === "ongoing")).map(t => t.id),
        [torks]
    );

    const PRE_TORK = new Set(['planned', 'unplanned', 'closednotstarted']);

    /* Fires the debounced bulk prio API call (no optimistic Redux dispatch). */
    const debouncedBulkPrioApi = (prioList) => {
        pendingPrioListRef.current = prioList;
        if (bulkPrioDebounceRef.current) clearTimeout(bulkPrioDebounceRef.current);
        bulkPrioDebounceRef.current = setTimeout(() => {
            const list = pendingPrioListRef.current;
            if (!list) return;
            pendingPrioListRef.current = null;
            BulkUpdatePrios(
                list,
                jwtToken,
                () => { /* success — Redux already has the correct state */ },
                () => { GetActiveOrders(jwtToken, (res) => dispatch(setTorkOrders(res))); }
            );
        }, 600);
    };

    /* Recompacts prios of a set of pre-tork orders (sorted by current prio → renumber 1…N).
       Dispatches optimistic Redux updates and fires the debounced API. */
    const recompactPrios = (preTorkOrders) => {
        const sorted = [...preTorkOrders].sort((a, b) => (a.prio || 0) - (b.prio || 0));
        const newPrioList = sorted.map((o, i) => ({ id: o.id, prio: i + 1 }));
        const changed = newPrioList.filter(({ id, prio }) =>
            sorted.find(o => o.id === id)?.prio !== prio
        );
        if (changed.length === 0) return;
        newPrioList.forEach(({ id, prio }) => {
            const order = sorted.find(o => o.id === id);
            if (order) dispatch(updateTorkOrder({ ...order, prio }));
        });
        debouncedBulkPrioApi(newPrioList);
    };

    /* ---- Orders ---- */
    const handleCreateOrders = (payload) => {
        return CreateOrders(payload, jwtToken, (created, hasActiveNotes) => {
            dispatch(addTorkOrders(created));
            // Renumber the pre-tork list 1…N with the new orders last (lowest prio).
            const existing = orders
                .filter(o => PRE_TORK.has(o.status) && !created.some(c => c.id === o.id))
                .sort((a, b) => (a.prio || 0) - (b.prio || 0));
            const newOnes = created.filter(o => PRE_TORK.has(o.status));
            recompactPrios([...existing, ...newOnes]);
            if (hasActiveNotes) {
                NotifyWarning("Varning: vald tork har aktiva noteringar (planerad/pågående).");
            }
            setShowCreate(false);
        });
    };

    const handleStartTork = (id, payload) => {
        StartTork(id, payload, jwtToken, (updated) => {
            dispatch(updateTorkOrder(updated));
            setStartOrder(null);
            // Order left the pre-tork list — recompact remaining
            const remaining = orders.filter(o => o.id !== id && PRE_TORK.has(o.status));
            recompactPrios(remaining);
        });
    };

    const handleStatusChange = (id, status) => {
        ChangeOrderStatus(id, status, null, jwtToken, (updated) => {
            const prevOrder = orders.find(o => o.id === id);
            const wasInList = prevOrder && PRE_TORK.has(prevOrder.status);
            const isNowInList = PRE_TORK.has(updated.status);

            if (updated.status === "closed") {
                dispatch(removeTorkOrder({ id: updated.id }));
            } else if (!wasInList && isNowInList) {
                // Order JOINED the list — give it the next prio and dispatch with updated status
                const maxPrio = orders
                    .filter(o => o.id !== id && PRE_TORK.has(o.status))
                    .reduce((max, o) => Math.max(max, o.prio || 0), 0);
                dispatch(updateTorkOrder({ ...updated, prio: maxPrio + 1 }));
                debouncedBulkPrioApi([{ id: updated.id, prio: maxPrio + 1 }]);
            } else {
                dispatch(updateTorkOrder(updated));
            }
            // Keep the history modal in sync if it's open for this order
            setHistoryOrder(prev => prev?.id === updated.id ? updated : prev);

            // Order LEFT the list — recompact remaining
            if (wasInList && !isNowInList) {
                const remaining = orders.filter(o => o.id !== id && PRE_TORK.has(o.status));
                recompactPrios(remaining);
            }
        });
    };

    const handleUpdateOrder = (order) => {
        UpdateOrder(order, jwtToken, (updated) => dispatch(updateTorkOrder(updated)));
    };

    const handleRetork = (id, { dryFinished, torkId }) => {
        const currentOrder = orders.find(o => o.id === id);
        StartTork(id, { dryFinished }, jwtToken, (updated) => {
            const torkChanged = torkId !== (currentOrder?.torkId || null);
            if (torkChanged) {
                UpdateOrder({ ...updated, torkId }, jwtToken, (final) => dispatch(updateTorkOrder(final)));
            } else {
                dispatch(updateTorkOrder(updated));
            }
        });
    };

    /* Debounced bulk prio update: optimistically applies the new prio list to Redux
       immediately, then fires one API call after 600ms of inactivity.
       On API error, re-fetches active orders to reconcile state. */
    const handleBulkUpdatePrios = (prioList) => {
        prioList.forEach(({ id, prio }) => {
            const order = orders.find(o => o.id === id);
            if (order) dispatch(updateTorkOrder({ ...order, prio }));
        });
        debouncedBulkPrioApi(prioList);
    };

    const handleDeleteOrder = (id) => {
        DeleteOrder(id, jwtToken, () => {
            dispatch(removeTorkOrder({ id }));
            // Recompact remaining pre-tork orders
            const remaining = orders.filter(o => o.id !== id && PRE_TORK.has(o.status));
            recompactPrios(remaining);
        });
    };

    const handleShowHistory = (order) => {
        setHistoryOrder(order);
    };

    /* ---- Torks ---- */
    const handleCreateTork = (tork) => CreateTork(tork, jwtToken, (res) => dispatch(addTork(res)));
    const handleUpdateTork = (tork) => UpdateTork(tork, jwtToken, (res) => dispatch(updateTork(res)));
    const handleDeleteTork = (id) => DeleteTork(id, jwtToken, () => dispatch(removeTork({ id })));
    const handleAddNote = (torkId, note) => AddTorkNote(torkId, note, jwtToken, (res) => dispatch(updateTork(res)));
    const handleUpdateNote = (torkId, noteId, note) => UpdateTorkNote(torkId, noteId, note, jwtToken, (res) => dispatch(updateTork(res)));
    const handleNoteStatus = (torkId, noteId, status) =>
        UpdateTorkNoteStatus(torkId, noteId, status, jwtToken, (res) => dispatch(updateTork(res)));
    const handleDeleteNote = (torkId, noteId) =>
        DeleteTorkNote(torkId, noteId, jwtToken, (res) => dispatch(updateTork(res)));

    /* ---- Note types ---- */
    const reloadNoteTypes = () => GetNoteTypes(jwtToken, (res) => dispatch(setNoteTypes(res)));
    const handleCreateNoteType = (nt) => CreateNoteType(nt, jwtToken, reloadNoteTypes);
    const handleUpdateNoteType = (nt) => UpdateNoteType(nt, jwtToken, reloadNoteTypes);
    const handleDeleteNoteType = (id) => DeleteNoteType(id, jwtToken, reloadNoteTypes);

    return (
        <MainLayout>
            <div className="p-6 overflow-y-auto h-full">
                {!hasTorkAccess() ? (
                    <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        </svg>
                        <p className="text-lg font-semibold text-gray-700 mb-1">Åtkomst nekad</p>
                        <p className="text-sm text-gray-500">Du saknar access till denna sida. Prata med <b>Veikko Tuomi</b> eller <b>Robin Boija</b> för access.</p>
                    </div>
                ) : (
                <>
                <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                    <h1 className="text-2xl font-semibold">Torkplanering</h1>
                    {!readOnly && (
                    <button
                        onClick={() => setActiveTab("settings")}
                        title="Inställningar"
                        className={
                            "p-2 rounded-lg border transition " +
                            (activeTab === "settings"
                                ? "bg-gray-200 border-gray-300 text-gray-800"
                                : "bg-white border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-700")
                        }
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </button>
                    )}
                </div>

                {activeTab === "settings" ? (
                    <div className="mb-4">
                        <button
                            onClick={() => setActiveTab("orders")}
                            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition mb-4"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                            Tillbaka till Torkar att starta
                        </button>
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Inställningar</h2>
                    </div>
                ) : (
                <div className="flex flex-wrap gap-2 border-b mb-4">
                    {TABS.map(tab => (
                        <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                            className={
                                "px-4 py-2 text-sm -mb-px border-b-2 " +
                                (activeTab === tab.key
                                    ? "border-blue-600 text-blue-600 font-medium"
                                    : "border-transparent text-gray-500 hover:text-gray-700")
                            }>
                            {tab.label}
                        </button>
                    ))}
                </div>
                )}

                {activeTab === "orders" && (
                    <div>
                        <div className="flex items-center justify-between gap-3 mb-3">
                            <SearchInput
                                value={orderSearch}
                                onChange={setOrderSearch}
                                placeholder="Sök Order-ID…"
                                className="w-full max-w-xs"
                            />
                            {!readOnly && (
                            <button onClick={() => setShowCreate(true)}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm shadow-sm transition shrink-0">
                                <span className="text-lg leading-none">+</span>
                                <span>Ny torkorder</span>
                            </button>
                            )}
                        </div>
                        <OrdersTable
                            orders={orders} torks={torks}
                            noteTypes={noteTypes}
                            search={orderSearch}
                            activeNoteTorkIds={activeNoteTorkIds}
                            onUpdate={handleUpdateOrder}
                            onStart={setStartOrder}
                            onStatusChange={handleStatusChange}
                            onDelete={handleDeleteOrder}
                            onShowHistory={handleShowHistory}
                            onBulkUpdatePrios={handleBulkUpdatePrios}
                            readOnly={readOnly}
                        />
                    </div>
                )}

                {activeTab === "empty" && (
                    <KilnsToEmpty
                        orders={orders} torks={torks} noteTypes={noteTypes}
                        onStatusChange={handleStatusChange}
                        onRetork={handleRetork}
                        onShowHistory={handleShowHistory}
                        readOnly={readOnly}
                    />
                )}

                {activeTab === "torks" && (
                    <div>
                        <div className="flex mb-4">
                            {[
                                { key: "cards", label: "Torkar" },
                                { key: "notes", label: "Noteringslista" },
                            ].map((view, i, arr) => (
                                <button
                                    key={view.key}
                                    onClick={() => setTorkView(view.key)}
                                    className={
                                        "px-3 py-1.5 text-sm border transition " +
                                        (i === 0 ? "rounded-l-lg " : "-ml-px ") +
                                        (i === arr.length - 1 ? "rounded-r-lg " : "") +
                                        (torkView === view.key
                                            ? "bg-blue-600 text-white border-blue-600 font-medium relative z-10"
                                            : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50")
                                    }
                                >
                                    {view.label}
                                </button>
                            ))}
                        </div>
                        {torkView === "cards" ? (
                            <TorksManager
                                torks={torks} noteTypes={noteTypes}
                                onCreate={handleCreateTork}
                                onUpdate={handleUpdateTork}
                                onDelete={handleDeleteTork}
                                onAddNote={handleAddNote}
                                onUpdateNote={handleUpdateNote}
                                onNoteStatus={handleNoteStatus}
                                onDeleteNote={handleDeleteNote}
                                readOnly={readOnly}
                            />
                        ) : (
                            <TorkNotesList torks={torks} noteTypes={noteTypes} />
                        )}
                    </div>
                )}

                {activeTab === "history" && (
                    <History allOrders={allOrders} torks={torks} onShowHistory={handleShowHistory} />
                )}

                {activeTab === "settings" && !readOnly && (
                    <NoteTypeSettings
                        noteTypes={noteTypes}
                        onCreate={handleCreateNoteType}
                        onUpdate={handleUpdateNoteType}
                        onDelete={handleDeleteNoteType}
                    />
                )}
                </>
                )}
            </div>

            {showCreate && (
                <CreateOrderForm onSubmit={handleCreateOrders} onClose={() => setShowCreate(false)} torks={torks} noteTypes={noteTypes} />
            )}

            {startOrder && (
                <StartTorkModal
                    order={startOrder}
                    torkNotes={torks.find(t => t.id === startOrder.torkId)?.notes || []}
                    noteTypes={noteTypes}
                    onSubmit={handleStartTork}
                    onClose={() => setStartOrder(null)}
                />
            )}

            {historyOrder && (
                <OrderHistoryModal
                    order={historyOrder}
                    onClose={() => setHistoryOrder(null)}
                    onStatusChange={handleStatusChange}
                />
            )}
        </MainLayout>
    );
};

export default TorkPlanning;
